import { ColumnFiltersState } from '@tanstack/react-table';
import { FilterType } from '@sib/types';
import { AccessorKeyColumnDef, SortingState } from '@tanstack/table-core';
import { AUTOCOMPLETE_PAGE_SIZE, DEFAULT_PAGE } from '../constants';
import {
  CrudFilter,
  CrudOperators,
  CrudSort,
  LogicalFilter,
  BaseKey,
} from '../types';

const mapOperatorsFromFilterType = (
  filterType: FilterType
): 'eq' | 'contains' | 'in' | 'between' | 'inci' | 'eqci' => {
  const operators: Record<
    FilterType,
    'eq' | 'contains' | 'in' | 'between' | 'inci' | 'eqci'
  > = {
    [FilterType.SELECT]: 'in',
    [FilterType.SINGLE_SELECT]: 'eq',
    [FilterType.CI_SELECT]: 'inci',
    [FilterType.CI_SINGLE_SELECT]: 'eqci',
    [FilterType.TEXT]: 'contains',
    [FilterType.NUMBER]: 'eq',
    [FilterType.DATE]: 'between',
  };

  return operators[filterType];
};

export const mapColumnFiltersToCrudFilters = <T = unknown>(
  columnFilters: ColumnFiltersState,
  columns?: AccessorKeyColumnDef<T>[]
): CrudFilter[] =>
  columnFilters.map(cf => {
    const column = columns?.find(c => c.accessorKey === cf.id);

    const filterType = column?.filter?.type;

    const operator = filterType
      ? mapOperatorsFromFilterType(filterType as FilterType)
      : mapOperatorsFromFilterType(FilterType.TEXT);

    return {
      field: cf.id,
      operator: operator,
      value: cf.value,
    };
  });

export const transformOperator = (operator: CrudOperators) => {
  if (operator === 'eq') return 'eq';
  if (operator === 'ne') return 'neq';
  if (operator === 'eqci') return 'eqci';
  if (operator === 'neqci') return 'neqci';
  if (operator === 'gt') return 'gt';
  if (operator === 'gte') return 'gte';
  if (operator === 'lt') return 'lt';
  if (operator === 'lte') return 'lte';
  if (operator === 'contains') return 'like';
  if (operator === 'ncontains') return 'nlike';
  if (operator === 'null') return 'isnull';
  if (operator === 'nnull') return 'isnotnull';
  if (operator === 'in') return 'in';
  if (operator === 'nin') return 'nin';
  if (operator === 'inci') return 'inci';
  if (operator === 'ninci') return 'ninc';
  if (operator === 'between') return 'between';

  return 'eq';
};

export const isLogicalFilter = (
  operator: CrudFilter
): operator is LogicalFilter => {
  if (operator.operator === 'and') return false;
  if (operator.operator === 'or') return false;

  return true;
};

export const getUrl = (resource: string, id?: BaseKey) => {
  if (id) return `api/${resource}/${id}`;

  return `api/${resource}`;
};

export const buildFilter = (filter: CrudFilter) => {
  if (isLogicalFilter(filter)) {
    return `${filter.field}:${transformOperator(filter.operator)}:${typeof filter.value === 'function' ? filter.value() : filter.value}`;
  }
  // TODO handle conditional operators

  return null;
};

export const buildSorter = (filter: CrudSort) =>
  `${filter.field}:${filter.order}`;

export const buildQueryFilters = (
  filters?: (CrudFilter | null | false)[]
): string[] =>
  (filters || [])
    .map(filter => filter && buildFilter(filter))
    .filter((item): item is string => !!item);

export const buildQueryArraySorters = (sorters?: CrudSort[]): string[] =>
  (sorters || [])
    .map(sorter => sorter && buildSorter(sorter))
    .filter((item): item is string => !!item);

export const buildQuerySorters = (sorters: CrudSort[]): string =>
  sorters.map(sorter => buildSorter(sorter)).join(',');

export const buildQuerySortingFromState = (state?: SortingState): string => {
  if (!state) return '';

  const sorters = state.map(({ desc, id }) =>
    buildSorter({
      field: id,
      order: desc ? 'desc' : 'asc',
    })
  );

  return sorters.join(',');
};

export const buildQuerySortingFromStateArray = (
  state?: SortingState
): string[] => {
  if (!state) return [];

  return state.map(({ desc, id }) =>
    buildSorter({
      field: id,
      order: desc ? 'desc' : 'asc',
    })
  );
};

export type { CrudFilter };

export const getAutoCompleteFilter = (searchTerm: string) => ({
  page: DEFAULT_PAGE,
  size: AUTOCOMPLETE_PAGE_SIZE,
  sort: [
    buildSorter({
      field: 'name',
      order: 'asc',
    }),
  ],
  filter: searchTerm
    ? buildQueryFilters([
        {
          field: 'name',
          value: searchTerm,
          operator: 'contains',
        },
      ])
    : [],
});
